import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SessionsChartContainer from '../../Sessions/Chart/SessionsChartContainer';
import RobotMessageComponent from '../../../Components/RobotMessageComponent/RobotMessageComponent';
import './SessionsContainer.css';
import SelectedRecord from '../../../Components/SelectedRecord/SelectedRecord';
import moment from 'moment-timezone';
import SessionsStats from '../../Sessions/Stats/SessionsStats';
import SessionsDateRangeSelector from '../../Sessions/Filters/SessionsDateRangeSelector';
import SessionsRangeFilter from '../../Sessions/Filters/SessionsRangeFilter';
import OrganizerFilter from '../Filters/OrganizerFilter';
import {
  getAdminSessions,
  getSessionCreators,
  resetSessionCreators
} from '../../../Reducks/AdminSessions/AdminSessionsActions';
import {
  toggleGenerateReportModal as generateReportModalToggle,
  ReportStatus
} from '../../../Reducks/Reports/ReportsActions';
import Tips from '../../../Components/Tips/Tips';
import { desktopSkeletonScreen, mobileSkeletonScreen } from '../../../SkeletonScreens/SkeletonScreen';
import { SESSIONS_OVERVIEW } from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { CREATORS_PAGE_COUNT, RobotMessageState } from '../../../Utils/Constants';
import SessionsTable from './Table/SessionsTable';
import AppConstants from '../../../AppConstants';
import { BuildReportButton } from '../AdminDashboard/Header/AnalyticsDashboardHeader';

export class SessionsContainer extends Component {
  static propTypes = {
    reduced_sessions: PropTypes.object,
    reduced_reports: PropTypes.object,
    tips: PropTypes.array,
    isBig: PropTypes.bool,
    accountKey: PropTypes.string.isRequired,
    getSessionCreators: PropTypes.func.isRequired,
    isRestrictReportDownloadEnabled: PropTypes.bool
  };

  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      focusedSessionKeyInTable: null,
      focusedSessionKeyInChart: null
    };
  }

  render() {
    const { sessions, fetching, error } = this.props.reduced_sessions;
    const isEmptyOrError = (sessions.allKeys && sessions.allKeys.length === 0 && !fetching) || error;
    const { isBig } = this.props;
    if (fetching) {
      if (isBig) {
        return <div className="sessionsContainer">{desktopSkeletonScreen()}</div>;
      } else {
        return <div className="sessionsContainer">{mobileSkeletonScreen()}</div>;
      }
    }
    return (
      <div className="sessionsContainer">
        {isBig && this.dateAndFilterRow()}
        {!isBig && this.selectedRecordRow()}
        {this.statsCardsRow()}
        {isBig && AppConstants.TIPS_ENABLED && this.tipsRow()}
        {isEmptyOrError ? (
          <RobotMessageComponent type={error ? RobotMessageState.ERROR_STATE : RobotMessageState.EMPTY_STATE} />
        ) : (
          <div className="chartAndTable">
            {this.chartRow()}
            {this.tableRow()}
          </div>
        )}
      </div>
    );
  }

  getDateFilterSessions = (fromTime, toTime) => {
    const { accountKey, getSessions, reduced_sessions, getSessionCreators } = this.props;
    const { creators, registrantRange, attendeeRange } = reduced_sessions.filter;
    getSessions(accountKey, fromTime, toTime, creators, registrantRange, attendeeRange);
    getSessionCreators(accountKey, fromTime, toTime, 0, null, CREATORS_PAGE_COUNT, true);
  };

  getOrganizerFilterSessions = creators => {
    const { accountKey, getSessions, reduced_sessions } = this.props;
    const { fromTime, toTime, registrantRange, attendeeRange } = reduced_sessions.filter;
    getSessions(accountKey, fromTime, toTime, creators, registrantRange, attendeeRange);
  };

  getFilteredSessionsByCount = filterOptions => {
    const { accountKey, getSessions, reduced_sessions } = this.props;
    const { registrantRange, attendeeRange } = filterOptions;
    const { fromTime, toTime, creators } = reduced_sessions.filter;
    getSessions(accountKey, fromTime, toTime, creators, registrantRange, attendeeRange);
  };

  dateAndFilterRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { t } = this.context;
    const {
      reduced_sessions,
      toggleGenerateReportModal,
      reduced_reports,
      isBig,
      accountKey,
      getSessionCreators,
      isRestrictReportDownloadEnabled,
      resetSessionCreators
    } = this.props;
    const { creators, creatorsFetching, filter } = reduced_sessions;
    const { reportFetchStatus } = reduced_reports;
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="overviewHeading">{t('header.overview')}</div>
        <div className="d-flex justify-content-between align-items-center spreadHorizontalItems-8">
          <OrganizerFilter
            isBig={true}
            uniqueId={'sessionsOrganizerFilterContainer'}
            reduced_sessions={reduced_sessions}
            getData={this.getOrganizerFilterSessions}
            getCreators={getSessionCreators}
            resetCreators={resetSessionCreators}
            creators={creators.creators}
            creatorsFetching={creatorsFetching}
            filteredCreators={filter.creators}
            fromTime={filter.fromTime}
            toTime={filter.toTime}
            nextPage={creators.nextPage}
            accountKey={accountKey}
            page={creators.page}
            mixpanelData={mixpanelData}
          />
          <SessionsRangeFilter
            isBig={true}
            isAdmin={true}
            uniqueId={'sessionsFilterInSessionsContainer'}
            mixpanelData={mixpanelData}
            reduced_sessions={reduced_sessions}
            filterSessionsByCount={this.getFilteredSessionsByCount}
          />
          <SessionsDateRangeSelector
            isBig={true}
            uniqueId={'sessionsDateRangeSelectorInContainer'}
            mixpanelData={mixpanelData}
            reduced_sessions={reduced_sessions}
            getSessions={this.getDateFilterSessions}
          />
          {!isRestrictReportDownloadEnabled && (
            <BuildReportButton
              toggleGenerateReportModal={toggleGenerateReportModal}
              disabled={reportFetchStatus === ReportStatus.FETCHING}
              isBig={isBig}
            />
          )}
        </div>
      </div>
    );
  };

  selectedRecordRow = () => {
    const { t } = this.context;
    return (
      <SelectedRecord
        defaultTitle={t('header.overview')}
        defaultSubTitle={this.getSelectedDateRangeString()}
        title={this.getSelectedWebinarName()}
        subTitle={this.getSelectedWebinarStartTime()}
      />
    );
  };

  statsCardsRow = () => {
    const { sessions, stats, trend, filter } = this.props.reduced_sessions;
    const totalSessions = sessions.filteredKeys.length;
    const selectedSessionsCount = sessions.filteredSelectedSessionKeys.length;
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { isBig } = this.props;
    return (
      <SessionsStats
        isBig={isBig}
        totalSessions={totalSessions}
        totalAttendees={stats.filteredSelectedTotalAttendees}
        totalRegistrants={stats.filteredSelectedTotalRegistrants}
        selectedSessionsCount={selectedSessionsCount}
        selectedSession={sessions.byKey[this.state.focusedSessionKeyInTable]}
        trendData={trend}
        filter={filter}
        mixpanelData={mixpanelData}
      />
    );
  };
  tipsRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    return <Tips contents={this.props.tips} mixpanelData={mixpanelData} />;
  };

  chartRow = () => {
    const { isBig, reduced_sessions } = this.props;
    return (
      <SessionsChartContainer
        focusSessionInTable={this.focusSessionInTable}
        focusedSessionKeyInChart={this.state.focusedSessionKeyInChart}
        isBig={isBig}
        sessions={reduced_sessions}
      />
    );
  };
  tableRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { isBig } = this.props;
    const { focusedSessionKeyInTable } = this.state;
    return (
      <SessionsTable
        uniqueId={'sesssionsTableInOverview'}
        isBig={isBig}
        sessionData={this.props.reduced_sessions.sessions}
        includeEngagementColumns={false}
        focusedSessionKeyInTable={focusedSessionKeyInTable}
        focusSessionInChart={this.focusSessionInChart}
        mixpanelData={mixpanelData}
      />
    );
  };

  getSelectedWebinarName = () => {
    const { sessions } = this.props.reduced_sessions;
    const { focusedSessionKeyInTable } = this.state;
    return focusedSessionKeyInTable ? sessions.byKey[focusedSessionKeyInTable].webinarName : null;
  };
  getSelectedWebinarStartTime = () => {
    const { t } = this.context;
    const { sessions } = this.props.reduced_sessions;
    const { focusedSessionKeyInTable } = this.state;
    const { startTime, timeZone } = focusedSessionKeyInTable ? sessions.byKey[focusedSessionKeyInTable] : {};
    if (startTime && timeZone) {
      return moment(startTime)
        .tz(timeZone)
        .format(t('formatting.dayDateTimeWithZone'));
    }
    return null;
  };

  getSelectedDateRangeString = () => {
    const { t } = this.context;
    const { fromTime, toTime } = this.props.reduced_sessions.filter;
    if (fromTime && toTime) {
      return `${moment(fromTime).format(t('formatting.date'))} - ${moment(toTime).format(t('formatting.date'))}`;
    }
    return null;
  };

  focusSessionInTable = sessionKey => {
    this.setState({ focusedSessionKeyInTable: sessionKey });
  };
  focusSessionInChart = sessionKey => {
    this.setState({ focusedSessionKeyInChart: sessionKey });
  };
}

export default connect(
  state => ({
    reduced_sessions: state.reduced_admin_sessions,
    accountKey: state.reduced_user.user.accountKey,
    isRestrictReportDownloadEnabled: state.reduced_user.entitlements.isRestrictReportDownloadEnabled,
    reduced_reports: state.reduced_reports
  }),
  {
    getSessions: getAdminSessions,
    getSessionCreators,
    toggleGenerateReportModal: generateReportModalToggle,
    resetSessionCreators
  }
)(SessionsContainer);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Admin/Sessions/SessionsContainer.js